import { Suspense, lazy } from 'react'
import { Outlet, Link, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../features/auth/hooks/useAuth'
import { Button } from '../shared/components/ui/button'
import { BrandLogo } from '../shared/components/brand/BrandLogo'
import { NotificationBell } from '../features/reports/components/NotificationBell'
import { useNotificationPolling } from '../features/reports/hooks/useNotificationPolling'
import { LogOutIcon } from 'lucide-react'

// Lazy: keeps the portal quote service out of the first-paint chunk
const PortalQuotesBadge = lazy(() => import('../features/portal/components/PortalQuotesBadge'))

/**
 * Client portal shell (Phase 6): deliberately minimal, mobile-first.
 * No arborist chrome — brand, notifications and sign-out over a two-tab
 * nav (Reports · Quotes, 008) and a scrollable content area.
 */
export const PortalLayout = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  // The bell fetches on mount; this keeps the badge fresh (SC-006)
  useNotificationPolling()

  const handleLogout = async () => {
    await logout()
    void navigate('/portal/login')
  }

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    `inline-flex h-11 items-center gap-2 border-b-2 px-3 text-sm font-medium transition-colors ${
      isActive
        ? 'border-primary text-foreground'
        : 'border-transparent text-muted-foreground hover:text-foreground'
    }`

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b bg-background">
        <div className="container mx-auto flex h-14 items-center justify-between px-4 lg:px-8">
          <Link to="/portal" className="flex items-center gap-2">
            <BrandLogo className="h-7 w-auto" />
            <span className="text-xs text-muted-foreground">Client portal</span>
          </Link>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void handleLogout()}
              aria-label="Sign out"
              className="gap-2"
            >
              <LogOutIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
        <nav aria-label="Portal" className="container mx-auto flex px-4 lg:px-8">
          <NavLink to="/portal" end className={tabClass}>
            Reports
          </NavLink>
          <NavLink to="/portal/quotes" className={tabClass}>
            Quotes
            {/* Quotes needing a response (SC-006) */}
            <Suspense fallback={null}>
              <PortalQuotesBadge />
            </Suspense>
          </NavLink>
        </nav>
      </header>
      <main className="container mx-auto px-4 py-4 lg:px-8 max-w-3xl">
        {user?.email && (
          <p className="text-xs text-muted-foreground mb-3 sm:hidden">Signed in as {user.email}</p>
        )}
        <Outlet />
      </main>
    </div>
  )
}
